// Recursive dispatcher for one field. Decides visibility, renders nothing for
// hidden fields, descends into groups (building the ResolutionLevel chain as it
// goes — see plan "Implementation Status"), and otherwise looks up the leaf
// component from the dispatch table. Steps 6–9 flesh out leaf components and
// step 8 adds repeatable add/remove controls; the descent + path + level-chain
// logic lives here.

import { useMemo, type ReactElement } from 'react'
import {
  GROUP_STYLE_CLASSES,
  isGroupField,
  isRepeatableGroup,
  type DynamicFormField,
  type GroupConfig,
  type ValidationError,
} from './types'
import { type ResolutionLevel, isFieldVisible } from './visibility'
import {
  FieldContext,
  useFieldContext,
  type ClassNameOverrides,
  type FieldContextValue,
} from './rendererContext'
import { Caption, captionText } from './caption'
import { newGroupRow } from './value'
import { errorIdFor } from './ids'
import { resolveFieldComponent } from './fields'

interface FieldNodeProps {
  field: DynamicFormField
  /** Resolution-level chain root-first, this field's level last. */
  levels: ResolutionLevel[]
  /** The value object holding this field's value (the last level's value). */
  valueObj: Record<string, unknown> | undefined
  /** Path of the parent container ("" at the form root). */
  pathPrefix: string
}

export function FieldNode({ field, levels, valueObj, pathPrefix }: FieldNodeProps) {
  const ctx = useFieldContext()

  // Visibility-hidden and explicitly hidden fields render nothing but keep
  // their value in the store.
  if (!isFieldVisible(field, levels)) return null
  if (field.hidden === true) return null
  if (!field.key) return null

  const path = pathPrefix ? `${pathPrefix}.${field.key}` : field.key
  const fieldValue = valueObj?.[field.key]

  if (isGroupField(field)) {
    return isRepeatableGroup(field) ? (
      <RepeatableGroupNode field={field} value={fieldValue} levels={levels} path={path} />
    ) : (
      <GroupNode field={field} value={fieldValue} levels={levels} path={path} />
    )
  }

  const Component = resolveFieldComponent(field.type, ctx.fieldComponents)
  const control = <Component field={field} path={path} value={fieldValue} errors={ctx.errors[path] ?? []} />
  const alertClass = controlAlertClass(field)
  return alertClass ? <div className={alertClass}>{control}</div> : control
}

// Alpaca drew a control with an `alert` fieldClass inside that alert box; markup
// draws its own.
function controlAlertClass(field: DynamicFormField): string | undefined {
  if (field.type === 'markup') return undefined
  const classes = String(field.config?.legacy_field_class ?? '').split(/\s+/)
  if (!classes.includes('alert')) return undefined

  const level = classes.find((c) => c.startsWith('alert-'))?.slice('alert-'.length) ?? 'info'
  const style = `alert_${level}`
  return Object.prototype.hasOwnProperty.call(GROUP_STYLE_CLASSES, style)
    ? GROUP_STYLE_CLASSES[style as keyof typeof GROUP_STYLE_CLASSES]
    : GROUP_STYLE_CLASSES.alert_info
}

function asObject(value: unknown): Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

interface GroupChrome {
  container: string
  legend: string
  /** `undefined` leaves the description unrendered (the historical default). */
  description: string | undefined
}

/**
 * Container/legend/description classes for a group `<fieldset>`, shared by
 * `GroupNode` and `RepeatableGroupNode`.
 *
 * With `config.style` set the style classes REPLACE the host's `group` /
 * `groupUnlabeled` slot — a real host's section-card chrome fights `alert`'s own
 * padding and background, so a styled group opts out of it. The style applies
 * whether or not the group is labeled: the label-based branch exists to keep
 * invisible wrapper groups from taking card chrome, and an explicit style is an
 * author decision that should win either way. Styled groups also render their
 * description by default (a callout with no body text is the obvious failure
 * mode); unstyled groups keep the opt-in gate and byte-identical output.
 *
 * `CommonConfig` has an open index signature, so `style` may hold anything —
 * it is only ever used as a key into `GROUP_STYLE_CLASSES`, never interpolated.
 * The lookup is own-property-only: a bare `[style]` walks the prototype chain, so
 * a `style` of `"toString"` or `"constructor"` would resolve to a function and be
 * interpolated into `className` instead of falling through to the unstyled branch.
 */
function groupChrome(field: DynamicFormField, classNames: ClassNameOverrides): GroupChrome {
  const style = isGroupField(field) ? field.config?.style : undefined
  const styleClass =
    typeof style === 'string' && Object.prototype.hasOwnProperty.call(GROUP_STYLE_CLASSES, style)
      ? GROUP_STYLE_CLASSES[style]
      : undefined

  if (styleClass) {
    return {
      container: `${styleClass} ${classNames.groupStyled ?? 'mb-3'}`,
      legend: classNames.groupStyledLegend ?? 'fs-6 fw-semibold',
      description: classNames.groupDescription ?? 'small text-muted mb-2',
    }
  }

  // Unlabeled groups are invisible layout/visibility wrappers — they never
  // take the labeled-group (section card) styling.
  return {
    container: field.label ? (classNames.group ?? 'mb-3') : (classNames.groupUnlabeled ?? 'mb-3'),
    legend: classNames.groupLegend ?? 'fs-6 fw-semibold',
    description: classNames.groupDescription,
  }
}

interface GroupNodeProps {
  field: DynamicFormField
  value: unknown
  levels: ResolutionLevel[]
  path: string
}

function GroupNode({ field, value, levels, path }: GroupNodeProps) {
  const { classNames } = useFieldContext()
  const children = isGroupField(field) ? (field.children ?? []) : []
  const childObj = asObject(value)
  const childLevels: ResolutionLevel[] = [...levels, { fields: children, value: childObj }]

  const chrome = groupChrome(field, classNames)

  return (
    <fieldset className={chrome.container}>
      {field.label ? (
        <legend className={chrome.legend}>
          <Caption field={field} text={field.label} />
        </legend>
      ) : null}
      {/* Group descriptions are opt-in via the classNames slot so hosts that
          never rendered them (historical default) keep identical output;
          styled groups default the slot on — see `groupChrome`. */}
      {field.label && field.description && chrome.description != null ? (
        <div className={chrome.description}>
          <Caption field={field} text={field.description} />
        </div>
      ) : null}
      {children.map((child) => (
        <FieldNode
          key={child.key}
          field={child}
          levels={childLevels}
          valueObj={childObj}
          pathPrefix={path}
        />
      ))}
    </fieldset>
  )
}

function RepeatableGroupNode({ field, value, levels, path }: GroupNodeProps) {
  const ctx = useFieldContext()
  const { classNames, setFieldValue, mode, errors } = ctx
  const children = isGroupField(field) ? (field.children ?? []) : []
  const config: GroupConfig = (isGroupField(field) ? field.config : undefined) ?? {}
  const chrome = groupChrome(field, classNames)
  const rows = Array.isArray(value) ? value : []

  const min = typeof config.repeat_min === 'number' ? config.repeat_min : 0
  const max = typeof config.repeat_max === 'number' ? config.repeat_max : Infinity
  const interactive = mode !== 'readonly'
  const canAdd = interactive && rows.length < max
  const canRemove = interactive && rows.length > min

  const actionLabels = (config.actionbar_labels ?? {}) as Record<string, string>
  const addLabel =
    actionLabels.add ?? (field.label ? `Add ${captionText(field, field.label)}` : 'Add')
  const removeLabel = actionLabels.remove ?? 'Remove'
  // Opt-out, not opt-in: groups authored before the flag existed keep their arrows.
  const reorderable = config.reorderable !== false
  const isTable = config.layout === 'table'

  const groupErrors: ValidationError[] = errors[path] ?? []

  const addRow = () => setFieldValue(path, [...rows, newGroupRow(field)])
  const removeRow = (index: number) =>
    setFieldValue(
      path,
      rows.filter((_, i) => i !== index),
    )
  const moveRow = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= rows.length) return
    const next = rows.slice()
    ;[next[index], next[target]] = [next[target], next[index]]
    setFieldValue(path, next)
  }

  const rowControls = (index: number) => (
    <div className="btn-group btn-group-sm" role="group" aria-label={`Row ${index + 1} controls`}>
      {reorderable ? (
        <>
          <button
            type="button"
            className="btn btn-outline-secondary"
            aria-label={`Move row ${index + 1} up`}
            disabled={!interactive || index === 0}
            onClick={() => moveRow(index, -1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            aria-label={`Move row ${index + 1} down`}
            disabled={!interactive || index === rows.length - 1}
            onClick={() => moveRow(index, 1)}
          >
            ↓
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="btn btn-outline-danger"
        aria-label={`${removeLabel} row ${index + 1}`}
        disabled={!canRemove}
        onClick={() => removeRow(index)}
      >
        {removeLabel}
      </button>
    </div>
  )

  const renderRowFields = (rowObj: Record<string, unknown>, rowPath: string) => {
    const rowLevels: ResolutionLevel[] = [...levels, { fields: children, value: rowObj }]
    return children.map((child) => (
      <FieldNode
        key={child.key}
        field={child}
        levels={rowLevels}
        valueObj={rowObj}
        pathPrefix={rowPath}
      />
    ))
  }

  const groupError =
    groupErrors.length > 0 ? (
      <div id={errorIdFor(path)} className={classNames.invalidFeedback ?? 'invalid-feedback d-block'}>
        {groupErrors.map((error, i) => (
          <div key={`${error.code}-${i}`}>{error.message}</div>
        ))}
      </div>
    ) : null

  const addButton = (
    <button
      type="button"
      className="btn btn-outline-primary btn-sm"
      disabled={!canAdd}
      onClick={addRow}
    >
      {addLabel}
    </button>
  )

  return (
    <fieldset className={chrome.container}>
      {field.label ? (
        <legend className={chrome.legend}>
          <Caption field={field} text={field.label} />
        </legend>
      ) : null}
      {field.label && field.description && chrome.description != null ? (
        <div className={chrome.description}>
          <Caption field={field} text={field.description} />
        </div>
      ) : null}
      {isTable ? (
        <TableRows
          ctx={ctx}
          children={children}
          rows={rows}
          path={path}
          rowControls={rowControls}
          renderRowFields={renderRowFields}
        />
      ) : (
        rows.map((row, index) => {
          const rowObj = asObject(row)
          const rowKey = (rowObj.__key as string | undefined) ?? `row-${index}`
          const rowPath = `${path}[${index}]`
          return (
            <div className="dff-repeatable-row border rounded p-3 mb-2" key={rowKey}>
              {renderRowFields(rowObj, rowPath)}
              {rowControls(index)}
            </div>
          )
        })
      )}
      {groupError}
      {addButton}
    </fieldset>
  )
}

interface TableRowsProps {
  ctx: FieldContextValue
  children: DynamicFormField[]
  rows: unknown[]
  path: string
  rowControls: (index: number) => ReactElement
  renderRowFields: (rowObj: Record<string, unknown>, rowPath: string) => ReactElement[]
}

/**
 * Table layout for a repeatable group: one column per child field (header from
 * its label) plus a controls column. Field labels are visually hidden inside
 * cells (the column header is the visible label) but stay associated for
 * accessibility — see `hideFieldLabels` in FieldShell.
 */
function TableRows({ ctx, children, rows, path, rowControls, renderRowFields }: TableRowsProps) {
  const tableCtx = useMemo<FieldContextValue>(() => ({ ...ctx, hideFieldLabels: true }), [ctx])
  return (
    <table className="table align-middle">
      <thead>
        <tr>
          {children.map((child) => (
            <th key={child.key} scope="col">
              {child.label ? <Caption field={child} text={child.label} /> : child.key}
            </th>
          ))}
          <th scope="col">
            <span className="visually-hidden">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <FieldContext.Provider value={tableCtx}>
          {rows.map((row, index) => {
            const rowObj = asObject(row)
            const rowKey = (rowObj.__key as string | undefined) ?? `row-${index}`
            const rowPath = `${path}[${index}]`
            const cells = renderRowFields(rowObj, rowPath)
            return (
              <tr key={rowKey}>
                {children.map((child, ci) => (
                  <td key={child.key}>{cells[ci]}</td>
                ))}
                <td>{rowControls(index)}</td>
              </tr>
            )
          })}
        </FieldContext.Provider>
      </tbody>
    </table>
  )
}
